import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';

import Layout from './layout';
import Spinner from './components/Spinner';
import ErrorBoundary from './components/ErrorBoundary';
import lazyWithRetry from './helpers/lazyWithRetry';
import QueryParamsContextProvider from './context/QueryParamsContext/QueryParamsContext';
import UserInfoContextProvider from './context/UserInfoContext/UserInfoContext';
import { ToasterContextProvider } from './context/ToasterContext/ToasterContext';

const Dashboard = lazyWithRetry(() => import('./Dashboard'));
const Tools = lazyWithRetry(() => import('./Tools'));
const TransfersList = lazyWithRetry(() => import('./TransfersList'));
const TransferDetails = lazyWithRetry(() => import('./TransferDetails'));
const BateDetails = lazyWithRetry(() => import('./BateDetails'));
const RegistrationsList = lazyWithRetry(() => import('./RegistrationsList'));
const RegistrationDetails = lazyWithRetry(
  () => import('./RegistrationDetails'),
);
const InvoicesList = lazyWithRetry(() => import('./InvoicesList'));
const GenericProceduresList = lazyWithRetry(
  () => import('./GenericProceduresList'),
);
const GenericProcedureDetails = lazyWithRetry(
  () => import('./GenericProcedureDetails'),
);
const TrafficReportsList = lazyWithRetry(() => import('./TrafficReportsList'));
const SettingsPage = lazyWithRetry(() => import('./SettingsPage'));
const MechanicalWarrantiesList = lazyWithRetry(
  () => import('./MechanicalWarrantiesList'),
);
const Webhooks = lazyWithRetry(() => import('./Webhooks'));

export default function SwipooPro(): JSX.Element {
  useEffect(() => {
    if (!window.$sleek) {
      window.$sleek = {
        toggle: () => null,
        shutdown: () => null,
        setUser: () => null,
      };
      window.SLEEK_PRODUCT_ID = 345949012;

      const script = document.createElement('script');
      script.src = 'https://client.sleekplan.com/sdk/e.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);
  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
      }}
    >
      <BrowserRouter>
        <ToasterContextProvider>
          <UserInfoContextProvider>
            <QueryParamsContextProvider>
              <Layout>
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-full">
                        <Spinner color="text-blue-700" size={10} />
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/transfers" element={<TransfersList />} />
                      <Route
                        path="/transfer/:transactionCode"
                        element={<TransferDetails />}
                      />
                      <Route path="/bate/:bateCode" element={<BateDetails />} />
                      <Route
                        path="/registrations"
                        element={<RegistrationsList />}
                      />
                      <Route
                        path="/registration/:registrationCode"
                        element={<RegistrationDetails />}
                      />
                      <Route
                        path="/generic-procedures/:kind"
                        element={<GenericProceduresList />}
                      />
                      <Route
                        path="/generic-procedure/:procedureCode"
                        element={<GenericProcedureDetails />}
                      />
                      <Route
                        path="/mechanical-warranties"
                        element={<MechanicalWarrantiesList />}
                      />
                      <Route path="/invoices" element={<InvoicesList />} />
                      <Route
                        path="/traffic-reports"
                        element={<TrafficReportsList />}
                      />
                      <Route path="/tools" element={<Tools />} />
                      <Route
                        path="/developers/webhooks"
                        element={<Webhooks />}
                      />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="*" element={<TransfersList />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Layout>
            </QueryParamsContextProvider>
          </UserInfoContextProvider>
        </ToasterContextProvider>
      </BrowserRouter>
    </PostHogProvider>
  );
}
